import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { SUPPORTED_LANGUAGES } from "./languages";
import { preloadDateLocale } from "@/lib/date-locale";

// Lazy-loaded language resources
const loadResources = async (lang: string, ns: string) => {
  try {
    const module = await import(`../locales/${lang}/${ns}.json`);
    return module.default;
  } catch {
    // Fallback to English
    if (lang !== "en") {
      const fallback = await import(`../locales/en/${ns}.json`);
      return fallback.default;
    }
    return {};
  }
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    debug: false,
    fallbackLng: "en",
    supportedLngs: ["en", "fr", "sw", "am", "ar", "so", "zh", "din"],
    defaultNS: "common",
    ns: ["common", "connect", "dialer", "portal", "pulse"],
    interpolation: { escapeValue: false },
    detection: {
      order: ["localStorage", "navigator"],
      caches: ["localStorage"],
      lookupLocalStorage: "elefa_language",
    },
    react: { useSuspense: false },
    resources: {},
    partialBundledLanguages: true,
  });

// Apply text direction to the document
function applyDirection(lang: string) {
  const langConfig = SUPPORTED_LANGUAGES.find((l) => l.code === lang);
  const dir = langConfig?.dir || "ltr";
  document.documentElement.dir = dir;
  document.documentElement.lang = lang;
}

// Helper to load a language pack on demand
export async function loadLanguage(lang: string) {
  preloadDateLocale(lang);
  if (i18n.hasResourceBundle(lang, "common")) {
    await i18n.changeLanguage(lang);
    applyDirection(lang);
    return;
  }

  const [common, connect, dialer, portal, pulse] = await Promise.all([
    loadResources(lang, "common"),
    loadResources(lang, "connect"),
    loadResources(lang, "dialer"),
    loadResources(lang, "portal"),
    loadResources(lang, "pulse"),
  ]);

  i18n.addResourceBundle(lang, "common", common, true, true);
  i18n.addResourceBundle(lang, "connect", connect, true, true);
  i18n.addResourceBundle(lang, "dialer", dialer, true, true);
  i18n.addResourceBundle(lang, "portal", portal, true, true);
  i18n.addResourceBundle(lang, "pulse", pulse, true, true);
  await i18n.changeLanguage(lang);
  applyDirection(lang);
}

// Detect initial language from storage or browser
const detectedLang = localStorage.getItem("elefa_language") || "en";
const initialLang = ["en", "fr", "sw", "am", "ar", "so", "zh", "din"].includes(detectedLang) ? detectedLang : "en";

// Load English always (fallback), plus the user's language if different
const langsToLoad = initialLang === "en" ? ["en"] : ["en", initialLang];

Promise.all(
  langsToLoad.flatMap((lang) => [
    loadResources(lang, "common").then((r) => i18n.addResourceBundle(lang, "common", r, true, true)),
    loadResources(lang, "connect").then((r) => i18n.addResourceBundle(lang, "connect", r, true, true)),
    loadResources(lang, "dialer").then((r) => i18n.addResourceBundle(lang, "dialer", r, true, true)),
    loadResources(lang, "portal").then((r) => i18n.addResourceBundle(lang, "portal", r, true, true)),
    loadResources(lang, "pulse").then((r) => i18n.addResourceBundle(lang, "pulse", r, true, true)),
  ])
).then(() => {
  i18n.changeLanguage(initialLang);
  applyDirection(initialLang);
  preloadDateLocale(initialLang);
});

export default i18n;
